import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import * as yup from 'yup';
import { TestContext } from 'yup';

import { DEFAULT_DISPLAY_DATE_FORMAT } from 'core/constants';
import { getEmptyErrorMessage } from 'core/yup';
import { SpaPriorityTier } from 'quotes/components/ExportSkuPart/enums';
import { AttributeProjectKind, SpaStatus } from 'quotes/enums';
import { ISpaAudienceRights } from 'quotes/types';

import { getAttributeProjectKind } from './mappers';

// Named so the form can clear this error alone once one of the two "All" options is removed.
export const ALL_CUSTOMERS_ALL_SALES_LOCATIONS_TEST =
  'all-customers-all-sales-locations-permission';

// Enforces exactly one of project / customers (a project XOR at least one customer or
// All Customers).
const validateProjectXorCustomer = (
  project: { id?: number } | null | undefined,
  customers: { id?: number }[] | undefined,
  allCustomers: boolean | undefined
) => {
  const hasProject = !!project?.id;
  const hasCustomer = (customers?.length ?? 0) > 0 || !!allCustomers;

  return hasProject !== hasCustomer;
};

const NO_SPA_AUDIENCE_RIGHTS: ISpaAudienceRights = {
  canAllowAllCustomersAllLocationsSpa: false,
  canAllowAllCustomersSpa: false,
  canAllowProgramSpa: false,
};

/**
 * @param validUntil the quote's Valid Until, when the caller knows it. Omitted on VQ import,
 *                   where the quote does not exist yet — the BE still enforces the same rule.
 * @param spaAudienceRights the user's audience rights. The All Customers option and a program each
 *                   need their own right; combining either with All Sales Locations also needs the
 *                   all-locations one. A quote stored beyond the user's rights is refused too, so
 *                   passing the step requires narrowing the selection — the BE refuses it on save
 *                   as well.
 * @param canChooseSpaStatus the user's Allow Live or Planning SPA permission. Without it the SPA
 *                   status field is hidden and never sent, so it is not required.
 */
export const attributeSchema = (
  validUntil?: string | null,
  {
    canAllowAllCustomersSpa,
    canAllowAllCustomersAllLocationsSpa,
    canAllowProgramSpa,
  }: ISpaAudienceRights = NO_SPA_AUDIENCE_RIGHTS,
  canChooseSpaStatus = false
) =>
  yup
    .object({
      project: yup
        .object()
        .label(t('labelProject'))
        .nullable()
        .test(
          'project-xor-customer',
          t('messageExportSkuPartsProjectOrCustomerRequired'),
          function (this: TestContext, project) {
            return validateProjectXorCustomer(
              project,
              this.parent.customers,
              this.parent.allCustomers
            );
          }
        )
        .test(
          'program-permission',
          t('messageSpaProgramNotPermitted'),
          (project) =>
            canAllowProgramSpa || AttributeProjectKind.PROGRAM !== getAttributeProjectKind(project)
        ),
      // Filled from the project, so empty means the project has no customer in Partfiniti.
      projectCustomers: yup
        .array()
        .of(yup.object())
        .default([])
        .test(
          'project-has-customers',
          t('messageSpaProjectHasNoCustomers'),
          function (this: TestContext, projectCustomers) {
            return (
              AttributeProjectKind.PROJECT !== getAttributeProjectKind(this.parent.project) ||
              (projectCustomers?.length ?? 0) > 0
            );
          }
        ),
      customers: yup
        .array()
        .of(yup.object({ id: yup.number().required() }))
        .label(t('labelCustomers'))
        .default([])
        .test(
          'customer-xor-project',
          t('messageExportSkuPartsProjectOrCustomerRequired'),
          function (this: TestContext, customers) {
            return validateProjectXorCustomer(
              this.parent.project,
              customers,
              this.parent.allCustomers
            );
          }
        )
        .test(
          'all-customers-permission',
          t('messageAllCustomersSpaNotPermitted'),
          function (this: TestContext) {
            return canAllowAllCustomersSpa || !this.parent.allCustomers;
          }
        ),
      allCustomers: yup.boolean().default(false),
      // Standalone Sales/Transaction member — independent of the project XOR customers rule.
      allSalesLocations: yup.boolean().default(false),
      selectableSpaEnabled: yup.boolean().default(false),
      spaPriorityTier: yup
        .number()
        .label(t('labelSpaPriority'))
        .oneOf([SpaPriorityTier.PRIORITY_1, SpaPriorityTier.PRIORITY_2, SpaPriorityTier.PRIORITY_3])
        .required(getEmptyErrorMessage(t('labelSpaPriority'))),
      spaStatus: canChooseSpaStatus
        ? yup
            .string()
            .label(t('labelSpaStatus'))
            .oneOf(Object.values(SpaStatus))
            .required(getEmptyErrorMessage(t('labelSpaStatus')))
        : yup.string().notRequired(),
      // "All Locations" empties the array and raises the flag, so the requirement is satisfied
      // by either one.
      salesLocations: yup
        .array()
        .of(yup.object())
        .label(t('labelSalesLocations'))
        .default([])
        .test(
          'sales-location-required',
          t('messageExportSkuPartsSalesLocationRequired'),
          function (this: TestContext, salesLocations) {
            return (salesLocations?.length ?? 0) > 0 || !!this.parent.allSalesLocations;
          }
        )
        .test(
          ALL_CUSTOMERS_ALL_SALES_LOCATIONS_TEST,
          t('messageAllCustomersAllSalesLocationsNotAllowed'),
          function (this: TestContext) {
            const isAllCustomersAudience =
              !!this.parent.allCustomers ||
              AttributeProjectKind.PROGRAM === getAttributeProjectKind(this.parent.project);

            return (
              canAllowAllCustomersAllLocationsSpa ||
              !(isAllCustomersAudience && this.parent.allSalesLocations)
            );
          }
        ),
      shippingLocations: yup
        .array()
        .of(yup.object())
        .label(t('labelShippingLocations'))
        .default([]),
      tradeAgreementOwner: yup
        .object()
        .label(t('labelTradeAgreementOwner'))
        .nullable()
        .required(getEmptyErrorMessage(t('labelTradeAgreementOwner'))),
      // Required, yet the default stays blank: a prefilled "today" would silently date every
      // agreement the day it was configured, which is what the export used to do. A half-typed
      // date must also fail here rather than reach the BE: the mappers would serialize an invalid
      // Dayjs to the literal "Invalid Date" and the user would get a server error instead of an
      // inline one.
      tradeAgreementStartDate: yup
        .mixed<Dayjs>()
        .label(t('labelTradeAgreementStartDate'))
        .nullable()
        .default(null)
        .required(t('errorTradeAgreementStartDateRequired'))
        .test(
          'trade-agreement-start-date-valid',
          t('errorDateNotValid', { label: t('labelTradeAgreementStartDate') }),
          (value) => !value || dayjs(value).isValid()
        )
        // A start after the quote's Valid Until would send Flintfox an agreement that is never
        // active. Compared by day, so a same-day pair (a one-day agreement) stays valid. A start
        // in the past is deliberately allowed, and a blank Valid Until means no expiration.
        .test(
          'trade-agreement-start-date-not-after-valid-until',
          // Names the date actually compared against, so a rejection is self-explaining.
          t('errorTradeAgreementStartDateAfterValidUntil', {
            validUntil:
              validUntil && dayjs(validUntil).isValid()
                ? dayjs(validUntil).format(DEFAULT_DISPLAY_DATE_FORMAT)
                : validUntil,
          }),
          (value) => {
            if (!value || !validUntil) {
              return true;
            }

            const start = dayjs(value);
            const end = dayjs(validUntil);

            return !start.isValid() || !end.isValid() || !start.isAfter(end, 'day');
          }
        ),
    })
    // Defensively keep the "all" flags and their specific lists mutually exclusive at the
    // schema layer regardless of how they were set.
    .transform((value) => {
      if (!value) {
        return value;
      }

      let next = value;

      if (next.allCustomers) {
        next = { ...next, customers: [] };
      }

      if (next.allSalesLocations) {
        next = { ...next, salesLocations: [] };
      }

      return next;
    });
